/**
 * One notification per new message across this browser's tabs. Every tab gets the SignalR event; the first to
 * claim it (conversation id + time) tells the others over a BroadcastChannel, and they stay quiet. A visible tab
 * claims at once; a hidden tab waits `HIDDEN_TAB_DELAY_MS` first, so a tab the user is looking at wins. Best
 * effort: without BroadcastChannel each tab notifies on its own.
 */
export const INBOX_NOTIFY_CHANNEL = 'scrips-inbox-notify';
export const HIDDEN_TAB_DELAY_MS = 600;

/** Keys remembered, so the set cannot grow without end in a tab left open for days. */
const MAX_REMEMBERED = 200;

export interface TabClaims {
  /** True for the first claim of `key` in this browser (as far as this tab has heard); false after. */
  claim(key: string): boolean;
  close(): void;
}

type ChannelCtor = new (name: string) => BroadcastChannel;

export function createTabClaims(
  Channel: ChannelCtor | undefined = typeof BroadcastChannel === 'undefined' ? undefined : BroadcastChannel,
): TabClaims {
  const seen = new Set<string>();
  const remember = (key: string) => {
    seen.add(key);
    if (seen.size > MAX_REMEMBERED) seen.delete(seen.values().next().value as string);
  };
  let channel: BroadcastChannel | null = null;
  try {
    if (Channel) {
      channel = new Channel(INBOX_NOTIFY_CHANNEL);
      channel.onmessage = (e: MessageEvent) => {
        const key = (e.data as { claim?: unknown } | null)?.claim;
        if (typeof key === 'string') remember(key);
      };
    }
  } catch {
    channel = null;
  }
  return {
    claim(key) {
      if (seen.has(key)) return false;
      remember(key);
      try {
        channel?.postMessage({ claim: key });
      } catch {
        // The other tabs may notify too; nothing worse.
      }
      return true;
    },
    close() {
      channel?.close();
    },
  };
}
